import React, { useState, useEffect, useCallback } from 'react';
import { Card, Spinner, Table, Button } from 'react-bootstrap';
import { RefreshCw } from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
// Generated by Copilot — Migrated from Userfront to Clerk
import useAuthHeaders from '../hooks/useAuthHeaders';
import './LoginStatus.css';

const API_URL_V2 = process.env.REACT_APP_API_URL_V2;

const COLORS = ['#13a0ff', '#00d4aa', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

const formatTimestamp = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  return d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
};

const LoginStatus = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rawRows, setRawRows] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [seriesNames, setSeriesNames] = useState([]);
  const { getToken } = useAuthHeaders();

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const token = await getToken();
      const response = await fetch(`${API_URL_V2}/Status/login-status`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `Error ${response.status}`);
      }

      const data = await response.json();
      const rows = data.rows || [];
      setRawRows(rows);

      // Transform for recharts: group by timestamp, each series becomes a key
      const uniqueSeries = [...new Set(rows.map(r => r.series))];
      setSeriesNames(uniqueSeries);

      const byTimestamp = {};
      rows.forEach(row => {
        const ts = row.timestamp;
        if (!byTimestamp[ts]) {
          byTimestamp[ts] = { timestamp: ts };
        }
        byTimestamp[ts][row.series] = row.avgDurationSec;
      });

      const sorted = Object.values(byTimestamp).sort(
        (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
      );
      setChartData(sorted);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <div className="login-status-section">
      <div className="section-header mb-3">
        <h5 className="section-title">Login Status</h5>
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={fetchData}
          disabled={loading}
          className="refresh-btn"
        >
          <RefreshCw size={14} className={loading ? 'spinning' : ''} />
          <span className="ms-1">Actualizar</span>
        </Button>
      </div>

      {loading && rawRows.length === 0 && (
        <div className="text-center py-4">
          <Spinner animation="border" variant="primary" />
          <p className="text-muted mt-2">Cargando datos de Application Insights...</p>
        </div>
      )}

      {error && (
        <div className="error-box mb-3">
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Time Chart */}
      {chartData.length > 0 && (
        <Card className="shadow-sm border-0 mb-4">
          <Card.Body className="p-3">
            <div className="chart-title mb-2">Duración Promedio de Login (últimas 8 horas)</div>
            <ResponsiveContainer width="100%" height={320}>
              <LineChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
                <XAxis
                  dataKey="timestamp"
                  tickFormatter={formatTimestamp}
                  tick={{ fontSize: 12, fill: '#6c757d' }}
                />
                <YAxis
                  tick={{ fontSize: 12, fill: '#6c757d' }}
                  label={{ value: 'seg', angle: -90, position: 'insideLeft', style: { fontSize: 12, fill: '#6c757d' } }}
                />
                <Tooltip
                  labelFormatter={(val) => new Date(val).toLocaleString('es-MX')}
                  formatter={(value) => [`${value} seg`]}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {seriesNames.map((name, i) => (
                  <Line
                    key={name}
                    type="monotone"
                    dataKey={name}
                    stroke={COLORS[i % COLORS.length]}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    activeDot={{ r: 5 }}
                    connectNulls
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </Card.Body>
        </Card>
      )}

      {/* Raw Data Table */}
      {rawRows.length > 0 && (
        <Card className="shadow-sm border-0">
          <Card.Body className="p-3">
            <div className="chart-title mb-2">Datos ({rawRows.length} registros)</div>
            <div className="table-wrap">
              <Table className="modern-table" hover size="sm">
                <thead>
                  <tr>
                    <th>Timestamp</th>
                    <th>Serie</th>
                    <th className="text-end">Avg Duration (seg)</th>
                  </tr>
                </thead>
                <tbody>
                  {rawRows.map((row, i) => (
                    <tr key={i}>
                      <td>{new Date(row.timestamp).toLocaleString('es-MX')}</td>
                      <td>{row.series}</td>
                      <td className="text-end font-monospace">{row.avgDurationSec}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>
      )}

      {!loading && !error && rawRows.length === 0 && (
        <Card className="shadow-sm border-0">
          <Card.Body className="p-4 text-center text-muted">
            No hay datos de login en las últimas 8 horas.
          </Card.Body>
        </Card>
      )}
    </div>
  );
};

export default LoginStatus;
